import React, { useEffect, useMemo, useState } from "react";
import ModalWrapper from "./ModalWrapper";
import AuthInput from "../Input/AuthInput";
import AuthBtn from "../Buttons/AuthBtn";
import PaymentAccountSelector from "../Selector/PaymentAccountSelector";
import { fetchAccounts } from "../../store/Slices/AccountSlice";
import { ErrorToast, SuccessToast, WarningToast } from "../../utils/ShowToast";
import { useDispatch } from "react-redux";
import { createCashAdjustment } from "../../store/Slices/CashAdjustmentSlice";
import { FiCheckCircle } from "react-icons/fi";
import { toLocalYmd } from "../../utils/dateTime";

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
};

const AddCashAdjustmentModal = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  reportContext = null,
}) => {
  const dispatch = useDispatch();
  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(toLocalYmd());
  const [dateConfirmed, setDateConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const lockedDate = reportContext?.fixedDate || null;
  const effectiveDate = lockedDate || date;

  useEffect(() => {
    if (!open) return;
    dispatch(fetchAccounts());
    setDateConfirmed(false);
    if (lockedDate) {
      setDate(lockedDate);
    } else if (reportContext?.dateMax) {
      setDate(reportContext.dateMax);
    } else {
      setDate(toLocalYmd());
    }
  }, [open, lockedDate, reportContext?.dateMax, dispatch]);

  const effectiveLoading = saving || loading;
  const canSave = useMemo(
    () =>
      fromAccount?._id &&
      toAccount?._id &&
      String(fromAccount?._id) !== String(toAccount?._id) &&
      Number(amount) > 0 &&
      (!reportContext?.requireConfirm || dateConfirmed),
    [fromAccount, toAccount, amount, reportContext?.requireConfirm, dateConfirmed]
  );

  const resetForm = () => {
    setFromAccount("");
    setToAccount("");
    setAmount("");
    setDescription("");
    setDateConfirmed(false);
    setDate(
      lockedDate ||
        reportContext?.dateMax ||
        toLocalYmd()
    );
  };

  const validateReportDate = () => {
    if (!reportContext) return true;
    const { dateMin, dateMax } = reportContext;
    if (dateMin && effectiveDate < dateMin) {
      WarningToast(`Adjustment date must be on or after ${formatDisplayDate(dateMin)}`);
      return false;
    }
    if (dateMax && effectiveDate > dateMax) {
      WarningToast(`Adjustment date must be on or before ${formatDisplayDate(dateMax)}`);
      return false;
    }
    if (reportContext.requireConfirm && !dateConfirmed) {
      WarningToast("Please confirm the adjustment date");
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fromAccount?._id || !toAccount?._id) {
      WarningToast("Please select both accounts");
      return;
    }
    if (String(fromAccount._id) === String(toAccount._id)) {
      WarningToast("From and To account cannot be same");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      WarningToast("Please enter a valid amount");
      return;
    }
    if (!validateReportDate()) return;

    const adjustmentDescription = reportContext
      ? [description, `[Daily Report · ${formatDisplayDate(effectiveDate)}]`]
          .filter(Boolean)
          .join(" ")
          .trim()
      : description;

    try {
      setSaving(true);
      const result = await dispatch(
        createCashAdjustment({
          fromAccountId: fromAccount._id,
          toAccountId: toAccount._id,
          amount: Number(amount),
          date: effectiveDate,
          description: adjustmentDescription,
        })
      );
      if (createCashAdjustment.fulfilled.match(result)) {
        SuccessToast("Cash adjustment added successfully");
        dispatch(fetchAccounts());
        if (typeof onSubmit === "function") onSubmit(result.payload);
        resetForm();
        if (typeof onClose === "function") onClose();
      } else {
        ErrorToast(result.payload || "Failed to add cash adjustment");
      }
    } catch (error) {
      ErrorToast("Failed to add cash adjustment");
    } finally {
      setSaving(false);
    }
  };

  const periodLabel =
    reportContext?.dateMin &&
    reportContext?.dateMax &&
    reportContext.dateMin !== reportContext.dateMax
      ? `${formatDisplayDate(reportContext.dateMin)} → ${formatDisplayDate(reportContext.dateMax)}`
      : formatDisplayDate(effectiveDate);

  return (
    <ModalWrapper open={open} setOpen={onClose}>
      <h2 className="text-2xl font-bold text-center py-2 text-ink">Add Cash Adjustment</h2>
      <p className="text-sm text-center text-muted pb-4">
        {reportContext
          ? "Transfer between accounts for this daily report"
          : "Move amount from one account to another"}
      </p>

      <div className="grid gap-3 min-w-[320px] max-w-[540px]">
        {reportContext && (
          <div className="rounded-lg border border-main/25 bg-brandSoft px-3 py-2.5">
            <p className="text-[11px] font-bold uppercase tracking-wide text-main/80">
              Report period check
            </p>
            <p className="text-sm font-semibold text-ink mt-0.5">
              {reportContext.label || "Daily report"}:{" "}
              <span className="text-main">{periodLabel}</span>
            </p>
            {lockedDate ? (
              <p className="text-xs text-muted mt-1 flex items-center gap-1">
                <FiCheckCircle className="text-success shrink-0" />
                Adjustment will be saved for{" "}
                <strong className="text-inkSecondary">
                  {formatDisplayDate(lockedDate)}
                </strong>
              </p>
            ) : (
              <p className="text-xs text-muted mt-1">
                Pick the date this transfer belongs to (within the report range).
              </p>
            )}
          </div>
        )}

        <PaymentAccountSelector
          label="From account"
          selectedAccount={fromAccount}
          setSelectedAccount={setFromAccount}
        />
        <PaymentAccountSelector
          label="To account"
          selectedAccount={toAccount}
          setSelectedAccount={setToAccount}
        />
        <AuthInput
          label="Amount"
          placeholder="Enter amount"
          Type="number"
          Value={amount}
          setValue={setAmount}
        />

        {lockedDate ? (
          <div className="relative mb-[15px] w-[297px] maxInputWidth font-[Quicksand]">
            <p className="absolute top-[-11px] left-3 w-fit bg-white h-[13px] text-[15px] font-bold InputLabel text-main">
              Adjustment date
            </p>
            <div className="px-3 py-2 border border-main/30 rounded-lg w-full bg-brandSoft font-bold text-main">
              {formatDisplayDate(lockedDate)}
            </div>
          </div>
        ) : reportContext ? (
          <div className="relative mb-[15px] w-[297px] maxInputWidth font-[Quicksand]">
            <p className="absolute top-[-11px] left-3 w-fit bg-white h-[13px] text-[15px] font-bold InputLabel text-main">
              Adjustment date
            </p>
            <input
              type="date"
              className="px-3 py-2 border border-line bg-card text-ink rounded-lg w-full outline-none InputText focus:border-main focus:ring-2 focus:ring-main/15"
              value={date}
              min={reportContext.dateMin}
              max={reportContext.dateMax}
              onChange={(e) => {
                setDate(e.target.value || "");
                setDateConfirmed(false);
              }}
            />
          </div>
        ) : (
          <AuthInput
            label="Date"
            placeholder="Select date"
            Type="date"
            Value={date}
            setValue={setDate}
          />
        )}

        <AuthInput
          label="Description"
          placeholder="Optional description"
          Value={description}
          setValue={setDescription}
        />

        {reportContext?.requireConfirm && (
          <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-line bg-subtle px-3 py-2.5 text-sm text-inkSecondary">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-main"
              checked={dateConfirmed}
              onChange={(e) => setDateConfirmed(e.target.checked)}
            />
            <span>
              I confirm this cash adjustment is for{" "}
              <strong className="text-ink">
                {formatDisplayDate(effectiveDate)}
              </strong>{" "}
              and will appear on the daily report for that date.
            </span>
          </label>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 mt-5">
        <AuthBtn
          title={effectiveLoading ? "Saving..." : "Save Adjustment"}
          onSubmit={handleSave}
          type={1}
          disabled={!canSave || effectiveLoading}
        />
        <AuthBtn title="Cancel" onSubmit={onClose} type={2} />
      </div>
    </ModalWrapper>
  );
};

export default AddCashAdjustmentModal;
